import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { api, authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [playerProfile, setPlayerProfile] = useState(null);
  const [refereeProfile, setRefereeProfile] = useState(null);
  const [facilityData, setFacilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const authCheckSequence = useRef(0);

  const checkAuth = useCallback(async () => {
    const sequence = ++authCheckSequence.current;
    const tokenAtStart = localStorage.getItem('leaguehub-token');
    const isLatestCheck = () => sequence === authCheckSequence.current;

    try {
      const response = await authAPI.getMe();
      if (!isLatestCheck() || localStorage.getItem('leaguehub-token') !== tokenAtStart) {
        return false;
      }
      setUser(response.data.user);
      setPlayerProfile(response.data.player_profile);
      setRefereeProfile(response.data.referee_profile);
      setFacilityData(response.data.facility || null);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      const currentToken = localStorage.getItem('leaguehub-token');
      // The interceptor can remove a rejected current token. Only a different
      // non-null token proves that a newer login must win this race.
      if (!isLatestCheck() || (currentToken !== tokenAtStart && currentToken !== null)) {
        return false;
      }
      setUser(null);
      setPlayerProfile(null);
      setRefereeProfile(null);
      setFacilityData(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      if (isLatestCheck()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Native login stores a bearer token; a fresh install has no session to restore.
    // Keep the web check: an HttpOnly cookie can authenticate without localStorage.
    if (Capacitor.isNativePlatform() && !localStorage.getItem('leaguehub-token')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (sessionId) => {
    try {
      const response = await authAPI.exchangeSession(sessionId);
      localStorage.setItem('leaguehub-token', response.data.token);
      setUser(response.data.user);
      setPlayerProfile(response.data.profile);
      setRefereeProfile(null);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('leaguehub-token');
      setUser(null);
      setPlayerProfile(null);
      setRefereeProfile(null);
      setIsAuthenticated(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/profile/player', profileData);
      setPlayerProfile(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteRefereeProfile = async () => {
    await api.delete('/profile/referee');
    setRefereeProfile(null);
  };

  const updateRefereeProfile = async (profileData) => {
    try {
      const response = await api.put('/profile/referee', profileData);
      setRefereeProfile(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const loginWithPassword = async ({ email, password }) => {
    const response = await authAPI.login({ email, password });
    localStorage.setItem('leaguehub-token', response.data.token);
    setUser(response.data.user);
    setPlayerProfile(response.data.profile);
    setFacilityData(response.data.facility || null);
    setRefereeProfile(null);
    setIsAuthenticated(true);
    return response.data;
  };

  const registerWithEmail = async ({ name, email, password, account_type }) => {
    const response = await authAPI.register({ name, email, password, account_type });
    localStorage.setItem('leaguehub-token', response.data.token);
    setUser(response.data.user);
    setPlayerProfile(response.data.profile);
    setFacilityData(response.data.facility || null);
    setRefereeProfile(null);
    setIsAuthenticated(true);
    return response.data;
  };

  const requestPhoneCode = async ({ phone, name, email }) => {
    const response = await authAPI.requestPhoneOtp({ phone, name, email });
    return response.data;
  };

  const verifyPhoneCode = async ({ phone, code }) => {
    const response = await authAPI.verifyPhoneOtp({ phone, code });
    localStorage.setItem('leaguehub-token', response.data.token);
    setUser(response.data.user);
    setPlayerProfile(response.data.profile);
    setRefereeProfile(null);
    setIsAuthenticated(true);
    return response.data;
  };

  const value = {
    user,
    playerProfile,
    refereeProfile,
    facilityData,
    loading,
    isAuthenticated,
    login,
    loginWithPassword,
    registerWithEmail,
    requestPhoneCode,
    verifyPhoneCode,
    logout,
    checkAuth,
    updateProfile,
    updateRefereeProfile,
    deleteRefereeProfile,
    setUser,
    setFacilityData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
